import axios from "axios";
import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { FiLoader } from "react-icons/fi";
import { useDispatch } from "react-redux";
import { useNavigate } from "react-router";
import { useNotification } from "../../contextApi/NotificationProvider";
import { getUserDetails } from "../../redux/ReduxSlices/authSlice";
import { apiEndpoints } from "../../services/apiConfig";
import { countryData } from "../../utils/CountryCode";

const PaymentPopup = ({ selectedPlan, setShowModal, currency }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const [paymentStatus, setPaymentStatus] = useState(null);
  const [dateTime, setDateTime] = useState(new Date());
  const [isLoading, setIsLoading] = useState(false);
  const { showNotification } = useNotification();

  const {
    register,
    handleSubmit,
    getValues,
    watch,
    setValue,
    formState: { errors },
  } = useForm();

  //   const currency = watch("currency");

  useEffect(() => {
    if (currency === "INR") {
      setValue("countryCode", "+91");
    } else {
      setValue("countryCode", "");
    }
  }, [currency]);

  useEffect(() => {
    const intervalId = setInterval(() => {
      setDateTime(new Date());
    }, 1000);

    return () => clearInterval(intervalId);
  }, []);

  useEffect(() => {
    const loadRazorpayScript = () => {
      const script = document.createElement("script");
      script.src = apiEndpoints.razorpayCheckout;
      script.async = true;
      document.body.appendChild(script);
    };
    loadRazorpayScript();
  }, []);

  useEffect(() => {
    if (paymentStatus) {
      const timer = setTimeout(() => setPaymentStatus(null), 4000);
      return () => clearTimeout(timer);
    }
  }, [paymentStatus]);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: currency,
    }).format(amount);
  };

  function getCurrentDateTime() {
    const now = new Date();

    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, "0");
    const day = String(now.getDate()).padStart(2, "0");

    const hours = String(now.getHours()).padStart(2, "0");
    const minutes = String(now.getMinutes()).padStart(2, "0");
    const seconds = String(now.getSeconds()).padStart(2, "0");

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  // const amountInINR = 1;
  const amountInINR = parseFloat(selectedPlan.price);
  const amountInUSD = parseFloat(selectedPlan.price);
  const amountInPaise = amountInINR * 100;
  const amountInCents = amountInUSD * 100;
  const finalAmount = currency == "INR" ? amountInPaise : amountInCents;
  const currentDateTime = getCurrentDateTime();

  const handleFailure = async (errorPayload) => {
    const token = localStorage.getItem("token");

    try {
      await axios.post(apiEndpoints.failedPayment, errorPayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });
    } catch (reportError) {
      console.error("Error reporting failed:", reportError);
    }
  };

  const handleSendPaymentDetails = async (response) => {
    // const cardHolderName = document.getElementById("cardholderName").value;

    setIsLoading(true);
    const cardHolderName = watch("cardholderName");
    const token = localStorage.getItem("token");

    const finalPayload = {
      company_id: localStorage.getItem("company_id"),
      razorpay_payment_id: response.id,
      razorpay_order_id: response.order_id,
      entity: response.entity,
      amount: response.amount,
      currency: response.currency,
      status: response.status,
      invoice_id: response.invoice_id,
      international: response.international,
      method: response.method,
      amount_refunded: response.amount_refunded,
      refund_status: response.refund_status,
      captured: response.captured,
      description: response.description,
      card_id: response.card_id,
      bank: response.bank,
      wallet: response.wallet,
      vpa: response.vpa,
      email: response.email,
      contact: response.contact,
      notes: { username: response.notes.username ?? "unknown username" },
      fee: response.fee,
      tax: response.tax,
      error_code: response.error_code,
      error_description: response.error_description,
      error_source: response.error_source,
      error_step: response.error_step,
      error_reason: response.error_reason,
      acquirer_data: response.acquirer_data,
      rayzorpay_unix_timestamp: response.created_at,
      upi: response.upi,
      card: response.card,
    };

    setIsLoading(true);

    try {
      await axios.post(apiEndpoints.sendPaymentDetails, finalPayload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      setPaymentStatus("success");

      setTimeout(() => {
        closeModal();

        sessionStorage.setItem("alertSuccessRedirected", "true");
        navigate("/payment-success", { state: response });
        // 
        dispatch(getUserDetails());
      }, 1000);
      setIsLoading(false);
    } catch (error) {

      console.error("Error sending payment data:", error);

      const errorPayload = {
        amount: response.amount,
        contact: response.contact,
        email: response.email,
        razorpay_order_id: response.id,
        date_time: currentDateTime ?? "2025-11-10 10:00:00",
        level: "4",
        problem: error.message,
      };

      // Call the reusable function
      await handleFailure(errorPayload);

      showNotification(
        "An error occurred while processing your payment data. Please retry",
        "error"
      );
      setPaymentStatus("failure");
      navigate("/payment-failure");
      //   setPaymentFailedPopup(true);
      setIsLoading(false);
    } finally {
      setIsLoading(false);
    }
  };

  const handlePaymentSuccess = async (response) => {
    const token = localStorage.getItem("token");
    const payload = {
      payment_id: response.razorpay_payment_id,
    };

    try {
      setIsLoading(true);
      await axios
        .post(apiEndpoints.getPaymentDetails, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
        .then(async (res) => {
          await handleSendPaymentDetails(res.data);
        });
      setIsLoading(false);
    } catch (error) {
      console.error("Error sending payment data:", error);

      //   const phone = document.getElementById("phoneNumber").value;
      //   const email = document.getElementById("email").value;
      const phone = watch("phoneNumber");
      const email = watch("email");

      const errorPayload = {
        amount: finalAmount,
        contact: phone,
        email: email,
        razorpay_order_id: response.razorpay_payment_id,
        date_time: currentDateTime ?? "2025-11-10 10:00:00",
        level: "3",
        problem: error.message,
      };

      // Call the reusable function
      await handleFailure(errorPayload);

      showNotification(
        "An error occurred while processing your payment data. Please retry",
        "error"
      );
      setPaymentStatus("failure");
      navigate("/payment-failure");
      //   setPaymentFailedPopup(true);
    } finally {
      setIsLoading(false); // Hide loader
    }
  };

  // First call
  const handleRazorPayOrder = async (c) => {
    // c.preventDefault();

    const token = localStorage.getItem("token");

    const payload = {
      amount: finalAmount,
      currency: currency,
    };

    try {
      await axios
        .post(apiEndpoints.createOrder, payload, {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
        })
        .then(async (data) => {
          await handlePayment(data);
        });
    } catch (error) {
      console.error("Error sending payment data:", error);

      //   const phone = document.getElementById("phoneNumber").value;
      //   const email = document.getElementById("email").value;
      const phone = watch("phoneNumber");
      const email = watch("email");

      const errorPayload = {
        amount: finalAmount,
        contact: phone,
        email: email,
        razorpay_order_id: "",
        date_time: currentDateTime ?? "2025-11-10 10:00:00",
        level: "1",
        problem: error.message,
      };

      // Call the reusable function
      await handleFailure(errorPayload);

      showNotification(
        "An error occurred while processing your payment data. Please retry",
        "error"
      );
      setPaymentStatus("failure");
      navigate("/payment-failure");
      //   setPaymentFailedPopup(true);
    }
  };

  const handlePayment = async (e) => {
    if (!e.data.data || !e.data.data.id) {
      showNotification(
        "Oops... Something went wrong. Please try again",
        "error"
      );

      return;
    }

    var rzId = e.data.data.id;

    // const name = document.getElementById("cardholderName").value;
    // const phone = document.getElementById("phoneNumber").value;
    // const email = document.getElementById("email").value;

    const name = watch("cardholderName");
    const phone = watch("phoneNumber");
    const email = watch("email");

    const nameRegex = /^[a-zA-Z\s]+$/;
    if (!name || !nameRegex.test(name)) {
      showNotification(
        "Cardholder Name must contain only characters",
        "error"
      );

      return;
    }

    const phoneRegex = /^[0-9]{10}$/;
    if (!phone || !phoneRegex.test(phone)) {
      showNotification("Phone Number must contain 10 digits", "error");

      return;
    }

    const emailRegex = /\S+@\S+\.\S+/;
    if (!email || !emailRegex.test(email)) {
      showNotification("Please enter a valid Email Address", "error");

      return;
    }

    const options = {
      key: process.env.REACT_APP_RAZORPAY_LIVE_KEY,
      // key: process.env.REACT_APP_RAZORPAY_TEST_KEY,
      amount: finalAmount,
      currency: currency,
      order_id: rzId,
      name: "Digikore Studio Limited",
      description: "Transaction from selfsync ai",
      handler: async function (response) {
        await handlePaymentSuccess(response);
      },
      prefill: {
        name: name,
        email: email,
        contact: phone,
      },
      theme: {
        color: "#61b1a7",
      },
      modal: {
        ondismiss: async function () {
          const errorPayload = {
            amount: finalAmount,
            contact: phone,
            email: email,
            razorpay_order_id: rzId,
            date_time: currentDateTime ?? "2025-11-10 10:00:00",
            level: "2",
            problem: "Error in Razorpay SDK",
          };

          // Call the reusable function
          await handleFailure(errorPayload);

          setPaymentStatus("failure");
          navigate("/payment-failure");
          //   setPaymentFailedPopup(true);
        },
      },
    };

    const rzp1 = new window.Razorpay(options);
    rzp1.open();
  };

  const closeModal = () => {
    setPaymentStatus(null);
    setShowModal(false);
  };

  const onSubmit = (data) => {
    handleRazorPayOrder();
    // handle payment here
  };
  return (
    <section
      className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4"
      aria-label="Payment Popup"
    >
      {isLoading && (
        <div className="fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/30">
          <FiLoader className="animate-spin text-[#822bda] text-4xl" />
        </div>
      )}

      <div className="fixed inset-0 bg-black/10 backdrop-blur-none flex items-center justify-center z-50 p-2">
        <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full max-h-screen overflow-y-auto">
          <div className="bg-white rounded-2xl shadow-xl max-w-4xl w-full overflow-hidden">
            {/* Header */}
            <div className="bg-[#f9fafb] p-6 text-black border-b">
              <h1 className="text-2xl font-bold max-md:text-xl mb-2">
                <span className="bg-gradient-to-r from-[#822bda] to-cyan-400 text-transparent bg-clip-text">
                  Subscribe to SelfSync
                </span>
              </h1>
              <p className="text-gray-600 mt-1 max-md:text-base">Complete your purchase</p>
            </div>

            {/* Body */}
            <div className="flex flex-col md:flex-row">
              {/* Left Column - Plan Details */}
              <div className="w-full md:w-1/2 max-md:px-4 max-md:py-0 p-6 border-r border-gray-200">
                <div className="bg-indigo-50 rounded-xl p-6 max-md:p-4 transform transition-all hover:scale-[1.02] duration-300 border border-indigo-100">
                  <div className="flex justify-between items-center mb-4">
                    <span className="text-lg max-md:text-base font-semibold text-gray-800">
                      SelfSync
                    </span>
                    <span className="text-xl font-bold text-black">
                      {formatCurrency(selectedPlan.price)}
                    </span>
                  </div>
                  <p className="text-gray-600 text-sm">{selectedPlan.title}</p>
                </div>

                <div className="border-t border-gray-200 pt-6 mt-6">
                  <div className="flex justify-between items-center">
                    <span className="text-gray-600">Subtotal</span>
                    <span className="text-lg font-bold text-black">
                      {formatCurrency(selectedPlan.price)}
                    </span>
                  </div>
                </div>

                <div className="flex items-center space-x-2 text-sm text-gray-500 mt-8">
                  <img
                    src="https://razorpay.com/favicon.png"
                    alt="Razorpay"
                    className="w-8 h-8"
                  />
                  <span>Powered by Razorpay</span>
                  <span className="text-xs bg-gray-100 px-2 py-1 rounded text-gray-600">
                    Secure Payment
                  </span>
                </div>
              </div>

              {/* Right Column - Payment Form */}
              <div className="w-full md:w-1/2 p-6 max-md:px-4 max-md:py-4">
                <h3 className="text-xl font-semibold max-md:text-base text-gray-800 mb-6 max-md:mb-4 max-md:mt-2">
                  Payment Information
                </h3>

                <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
                  {/* Customer Name */}
                  <div>
                    <label
                      htmlFor="cardholderName"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Customer Name <span className="text-purple-600">*</span>
                    </label>
                    <input
                      id="cardholderName"
                      placeholder="Enter your name"
                      type="text"
                      {...register("cardholderName", {
                        required: "Name is required",
                        minLength: {
                          value: 2,
                          message: "Name must be at least 2 characters",
                        },
                        pattern: {
                          value: /^[A-Za-z\s]+$/,
                          message: "Only letters and spaces allowed",
                        },
                      })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 transition-all"
                    />
                    {errors.cardholderName && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.cardholderName.message}
                      </p>
                    )}
                  </div>

                  {/* Phone Number with Country Code */}
                  <div>
                    <label
                      htmlFor="phoneNumber"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Phone Number <span className="text-purple-600">*</span>
                    </label>
                    <div className="flex">
                      {/* Country Code Dropdown */}
                      <div className="relative w-1/4 mr-2">
                        <select
                          {...register("countryCode", {
                            required: "Country code is required",
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 transition-all appearance-none bg-white"
                          defaultValue="+91" // Default to India
                        >
                          <option value={""}>Select</option>
                          {countryData.map((country, i) => (
                            <option key={i} value={country.code}>
                              {country.code} {country.name}
                            </option>
                          ))}
                        </select>
                        <div className="absolute inset-y-0 right-0 flex items-center pr-3 pointer-events-none">
                          <svg
                            className="w-5 h-5 text-gray-400"
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                          >
                            <path
                              strokeLinecap="round"
                              strokeLinejoin="round"
                              strokeWidth="2"
                              d="M19 9l-7 7-7-7"
                            ></path>
                          </svg>
                        </div>
                      </div>

                      {/* Phone Number Input */}
                      <div className="flex-1">
                        <input
                          id="phoneNumber"
                          type="tel"
                          placeholder="Enter your phone number"
                          {...register("phoneNumber", {
                            required: "Phone number is required",
                            pattern: {
                              value: /^[0-9]{10,15}$/,
                              message: "Enter a valid phone number (10-15 digits)",
                            },
                          })}
                          className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 transition-all"
                        />
                      </div>
                    </div>
                    {errors.phoneNumber && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.phoneNumber.message}
                      </p>
                    )}
                    {errors.countryCode && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.countryCode.message}
                      </p>
                    )}
                  </div>

                  {/* Email Address */}
                  <div>
                    <label
                      htmlFor="email"
                      className="block text-sm font-medium text-gray-700 mb-1"
                    >
                      Email Address <span className="text-purple-600">*</span>
                    </label>
                    <input
                      id="email"
                      type="email"
                      placeholder="Enter your email"
                      {...register("email", {
                        required: "Email is required",
                        pattern: {
                          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
                          message: "Enter a valid email address",
                        },
                      })}
                      className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-300 focus:border-purple-500 transition-all"
                    />
                    {errors.email && (
                      <p className="text-red-500 text-sm mt-1">
                        {errors.email.message}
                      </p>
                    )}
                  </div>

                  <button
                    type="submit"
                    className="w-full bg-purple-600 hover:bg-purple-700 text-white font-semibold py-3 px-4 rounded-lg shadow-md mt-6 transition-colors"
                  >
                    Pay {formatCurrency(selectedPlan.price)}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </div>
      </div>


    </section>
  );
};

export default PaymentPopup;
